import {
  ChangeDetectorRef,
  Component, HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { BaseProductDetailComponent } from '@components/products/base-product-detail.component';
import {
  FundListingBRConfiguration,
  FundListingComponentType,
  FundListingDataFormat,
  FundListingState,
  DropdownItem,
  FundId
} from '@types';
import { Logger } from '@utils/logger';
import { Subscription } from 'rxjs';
import { FundListingDisplayComponent } from '../fund-listing-display-component.interface';
import { FundListingService } from '../services/fund-listing.service';
import {
  FundCompareData,
  FundCompareDetail,
  FundCompareItem,
  FundComparePerformanceData
} from "@products/types/fund-compare.type";
import { AddFundsModal } from "@products/types/add-funds.type";
import { FundCompareService } from "@products/fund-listing/services/fund-compare.service";
import cloneDeep from 'lodash/cloneDeep';
import { CommonService } from '@services/common.service';
import { AnalyticsService } from "@services/analytics.service";
import { InvestNowService } from "@services/invest-now.service";
import { Location } from "@angular/common";

const logger = Logger.getLogger('HomeFundListingComponent');

@Component({
  selector: 'ft-fund-compare',
  templateUrl: './fund-compare.component.html',
  styleUrls: ['./fund-compare.component.scss'],
  providers: [CommonService]
})
export class FundCompareComponent
  extends BaseProductDetailComponent
  implements FundListingDisplayComponent, OnInit, OnDestroy {

  brConfiguration: FundListingBRConfiguration;
  dataSubscription: Subscription;
  componentType: FundListingComponentType;
  fundListingState: FundListingState;
  fundData: FundListingDataFormat[] | FundListingDataFormat[];
  sideFilterOptions: any;
  funds: any[] = [];
  isLoaded: boolean;
  filterName: string;
  fundCompareDetails: FundCompareDetail[] = [];
  fundCompareIds: FundId[] = [];
  fundsData: FundCompareData[] = [];
  addFundsData: AddFundsModal;
  isAddFundData: boolean = false;
  isFundsData: boolean = false;
  // Below two variables will be used to store the selection for each fund in comparison table.
  // Hence, created as object to store values accordingly.
  selectedPerformanceValue: object = {}; // properties will be of string type.
  selectedPlanIndex: object = {}; // properties will be of number type.
  getAddFund: boolean;
  isMobileView: boolean;
  isMobileAddClicked: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private fundCompareService: FundCompareService,
    private fundListingService: FundListingService,
    private commonService: CommonService,
    private analyticsService: AnalyticsService,
    private investNowService: InvestNowService,
    private location: Location,
  ) {
    super();
  }

  @HostListener("window:resize", ["$event"])
  onWindowResize($event: any = {}) {
    this.isMobileView = window.innerWidth < 900;
  }

  ngOnInit() {
    this.isMobileView = window.innerWidth < 900;
    super.ngOnInit();
    this.setEmptyPageDataOnLoad();
    // At first, load the fund compare list from local storage.
    let detStorage = JSON.parse(localStorage.getItem("compare-funds"));
    if (detStorage == null || detStorage.length === 0) {
      this.fundsData = []
    } else {
      this.fundsData = detStorage;
      this.getFundsData();
    }
    this.commonService.mobileAddFund.subscribe((status) => {
      if (status) {
        this.isMobileAddClicked = false;
      }
    })
  }

  async getFundsData() {
    this.isFundsData = true
    try {
      this.fundCompareDetails = await this.fundCompareService.retrieveFundDetailListFromLocalStorage();
      this.fundCompareService.getFundCompareState$().subscribe((state) => {
        logger.debug('Fund comparison state: ', state);
        this.isLoaded = state.isLoaded;
        if (state.isLoaded) {
          // Intentionally used 'cloneDeep' below, to avoid any modifications to state by use of splice later.
          this.fundsData = cloneDeep(state.fundsData);
          this.addFundsData = cloneDeep(state.addFundsData);
          // Assign default plan selection for each fund to be compared.
          this.handleDividendPlanSelection();
          this.fundsData.forEach((fund: any, index: number) => {
            this.getAnnualizedReturnsValue(fund?.performancePeriodDropdownItems?.[0]?.assetValue, index)
          })
          this.cdr.detectChanges();
        }
      });

      this.fundCompareService.populate();
    } catch (error) {
      logger.error('Error in communicating with local storage: ', error);
    }
  }

  getTopholding(data, sortLength) {
    let sortedTopTenList = [];
    let sortData = data.sort(function (a, b) {
      return a.rank - b.rank;
    });

    sortedTopTenList = sortData.slice(0, sortLength)

    return sortedTopTenList
  }

  /**
   * Triggered upon load, and the event emitter's value was set accordingly.
   */
  setEmptyPageDataOnLoad() {
    /** Call service to check fund is added or not */
    this.commonService.send_fund_data.subscribe((getFund) => {
      if (!this.isFundsData || getFund.isTrue) {
        this.getFundsData();
      }
      if (getFund == null || getFund.length === 0) {
        this.isAddFundData = false
      } else {
        this.isAddFundData = getFund.isTrue
        this.fundsData = getFund
      }
      // this.fundCompareService.populate();
    })
    this.isFundsData = false;
    this.cdr.detectChanges();
  }

  /**
   * Initialized when current commponent gets loaded.
   * @param brConfiguration bloomreach configuration received
   */
  populate(brConfiguration: FundListingBRConfiguration): void {
    // Save BR configuration only once.
    if (!this.brConfiguration) {
      this.brConfiguration = brConfiguration;
      this.brConfiguration.config.componentType = FundListingComponentType.PPSS;
    }

    this.dataSubscription = this.fundListingService
      .getFundInformationState$()
      ?.subscribe(
        async (state) => {
          logger.debug('Mapped fund listing state: ', state);
          //this.isLoaded = state.isLoaded;
          if (state.isLoaded) {
            this.componentType = this.brConfiguration.config.componentType;
            this.fundListingState = state;
            // side Options
            this.sideFilterOptions = state.filterDropdowns[0].filterItems.map((fund, index) => ({
              id: (index + 1).toString(),
              name: fund.label
            }));
            this.fundData = this.fundListingState?.ppssState?.ppssData ? JSON.parse(JSON.stringify(this.fundListingState?.ppssState?.ppssData)) : [];

            try {
              const storedFundCompareDetails: FundCompareDetail[] = await this.fundCompareService.retrieveFundDetailListFromLocalStorage();

              // Only save the valid list, else remain it initialized with an empty array.
              if (storedFundCompareDetails?.length) {
                this.fundCompareDetails = storedFundCompareDetails;
              }

              // Additionally, extract the fund ids and the fund names from the retrieved list
              // to pass the ids to the fund card and to show the sticky panel respectively.
              // But, first, reset the list for every new filter state, and, let it have the fresh list from 'fundCompareDetails'.
              this.fundCompareIds = [];
              this.fundCompareDetails.forEach((fundCompareDetail) => {
                this.fundCompareIds.push(fundCompareDetail.fundId);
              });
            } catch (error) {
              logger.error(
                'Error in communicating with local storage: ',
                error
              );
            }
          }
          this.cdr.detectChanges();
        },
        (error) => {
          logger.error('Error in invoking fund listing service: ', error);
          this.cdr.detectChanges();
        }
      );

    this.fundListingService.populate(this.brConfiguration);
  }

  /**
   * Triggered when an option is changed in performance period select dropdown.
   * @param selectedItem change event generated from select dropdown
   * @param fundIndex column index of the fund
   * @param performanceData fund data performance object containing period values
   */
  getAnnualizedReturnsValue(selectedItem: DropdownItem,
                            fundIndex: number) {
    this.selectedPerformanceValue['fund' + (fundIndex + 1)] =
      selectedItem;
  }

  handleDividendPlanSelection(event?: Event, fundIndex?: number): void {
    if (!event) {
      // Assign default plan for all funds to be compared, i.e., 0th dividend, if not called by change event.
      this.fundsData.forEach((fundData, index) => {
        this.selectedPlanIndex['fund' + (index + 1)] = 0;
      });
    } else {
      const selectedPlan: string = (event.target as HTMLSelectElement).value;
      const matchedSelectedPlanIndex: number = this.fundsData[
        fundIndex
        ].dividendData.findIndex(
        (dividend) => dividend.planName === selectedPlan
      );

      // Assign the new matched index for plan corresponding to fund being compare.
      this.selectedPlanIndex[
      'fund' + (fundIndex + 1)
        ] = matchedSelectedPlanIndex;
    }
  }

  makeCartToggleWeb() {
    const getToggle: boolean = false;
    sessionStorage.setItem('getToggle', JSON.stringify(getToggle));
    let detStorage = JSON.parse(localStorage.getItem("compare-funds"));

    if (detStorage !== null && detStorage.length > 0) {
      this.fundCompareIds = [];
      this.fundCompareDetails.forEach((fundCompareDetail) => {
        this.fundCompareIds.push(fundCompareDetail.fundId);
      });
    }
    this.getAddFund = true;
    this.isMobileView = window.innerWidth < 900;
    this.commonService.send_added_fund.emit(true);
    if (this.isMobileView) {
      this.isMobileAddClicked = true;
    }
    this.cdr.detectChanges();
  }

  /**
   * Remove the fund from comparison table.
   * @param fundIndex column index of the fund
   * @param fundId fund id of the fund being removed
   * @param fundName fund name of the fund being removed
   */
  removeFund(fundIndex: number, fundId: FundId, fundName: string): void {

    // First, remove it from class variable i.e., 'fundsData' collection.
    this.fundsData.splice(fundIndex, 1);

    // And, remove its id from add funds modal data.
    this.addFundsData.selectedFundIds.splice(fundIndex, 1);
    this.addFundsData = cloneDeep(this.addFundsData); // 'cloneDeep' to deep re-assign the updated add funds data.

    // Also, update the service for subsequent actions.
    let funds: FundCompareItem = {fundId: fundId, fundName: fundName};
    this.fundCompareService.addToCompareList(funds);
    // this.fundCompareService.handleRemoveFundAction(fundIndex, {
    //   fundId,
    //   fundName,
    // });

    if (this.fundsData.length == 0) {
      this.isAddFundData = false;
      this.fundsData = [];
    }
  }

  getRisk(productLabel: string) {
    let productLabelVal = ''
    if (productLabel !== null) {
      productLabelVal = this.commonService.getRisk(productLabel);
    }
    return productLabelVal
  }

  onInvestNow(name: any, id: any) {
    this.analyticsService.trackEvent({
      event: "Event FundPage",
      category: "Fund Page",
      action: "Invest Now",
      label: name,
    });
    this.investNowService.investNow(id);
  }

  goToCart() {
    window.location.href = '/funds-and-solutions/tools/cart';
  }

  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription?.unsubscribe();
    }
  }

  backClicked() {
    this.location.back();
  }

  mobileAddFundGoBack() {
    if (window.innerWidth < 900) {
      this.commonService.mobileAddFund.emit(true);
    }
  }
}
